import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, TrendingUp, DollarSign, Calendar } from 'lucide-react';
import { formatCurrency } from '../utils/currency';

export const LoanCalculator: React.FC = () => {
  const [amount, setAmount] = useState(500000);
  const [term, setTerm] = useState(12);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalRepayment, setTotalRepayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [interestRate] = useState(3.5); // 3.5% monthly (example rate)

  // Calculate loan details whenever amount or term changes
  useEffect(() => {
    calculateLoan();
  }, [amount, term]);

  const calculateLoan = () => {
    // Simple interest calculation for demonstration
    // Monthly interest rate (3.5% = 0.035)
    const monthlyRate = interestRate / 100;

    // Calculate monthly payment using reducing balance formula
    const principal = amount;
    const r = monthlyRate;
    const n = term;

    // Monthly payment formula: P * [r(1+r)^n] / [(1+r)^n - 1]
    const payment = principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

    const totalPaid = payment * n;
    const interest = totalPaid - principal;

    setMonthlyPayment(Math.round(payment));
    setTotalRepayment(Math.round(totalPaid));
    setTotalInterest(Math.round(interest));
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(parseInt(e.target.value));
  };

  const handleTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTerm(parseInt(e.target.value));
  };

  return (
    <section className="calculator-section">
      <div className="section-container">
        <div className="calculator-header">
          <div className="calculator-icon-wrapper">
            <Calculator size={40} className="calculator-icon" />
          </div>
          <h2>Calculate Your Loan</h2>
          <p className="section-subtitle">
            See what you can afford with our interactive loan calculator.
            Adjust the loan amount and term to find the perfect plan for you.
          </p>
        </div>

        <div className="calculator-widget">
          <div className="calculator-inputs">
            {/* Loan Amount Input */}
            <div className="calculator-input-group">
              <label htmlFor="amount" className="calculator-label">
                <DollarSign size={20} />
                <span>Loan Amount</span>
              </label>
              <div className="amount-display">{formatCurrency(amount)}</div>
              <input
                type="range"
                id="amount"
                min="50000"
                max="10000000"
                step="50000"
                value={amount}
                onChange={handleAmountChange}
                className="calculator-slider"
              />
              <div className="slider-labels">
                <span>₦50K</span>
                <span>₦10M</span>
              </div>
            </div>

            {/* Loan Term Input */}
            <div className="calculator-input-group">
              <label htmlFor="term" className="calculator-label">
                <Calendar size={20} />
                <span>Loan Term</span>
              </label>
              <div className="term-display">{term} months</div>
              <input
                type="range"
                id="term"
                min="1"
                max="36"
                step="1"
                value={term}
                onChange={handleTermChange}
                className="calculator-slider"
              />
              <div className="slider-labels">
                <span>1 month</span>
                <span>36 months</span>
              </div>
            </div>
          </div>

          {/* Results Display */}
          <div className="calculator-results">
            <div className="result-card result-card-primary">
              <div className="result-icon">
                <TrendingUp size={24} />
              </div>
              <div className="result-content">
                <div className="result-label">Monthly Payment</div>
                <div className="result-value">{formatCurrency(monthlyPayment)}</div>
              </div>
            </div>

            <div className="result-card">
              <div className="result-content">
                <div className="result-label">Total Repayment</div>
                <div className="result-value result-value-secondary">
                  {formatCurrency(totalRepayment)}
                </div>
              </div>
            </div>

            <div className="result-card">
              <div className="result-content">
                <div className="result-label">Total Interest</div>
                <div className="result-value result-value-secondary">
                  {formatCurrency(totalInterest)}
                </div>
              </div>
            </div>

            <div className="result-card">
              <div className="result-content">
                <div className="result-label">Interest Rate</div>
                <div className="result-value result-value-secondary">
                  {interestRate}% per month
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="calculator-cta">
            <Link to="/register" className="btn btn-primary btn-lg btn-calculator">
              <Calculator size={20} />
              Apply for This Loan
            </Link>
            <p className="calculator-note">
              No credit card required • Instant approval • Money in 24 hours
            </p>
          </div>

          {/* Disclaimer */}
          <div className="calculator-disclaimer">
            <p>
              * This is an estimate based on a {interestRate}% monthly interest rate.
              Actual rates may vary based on your credit profile and loan product selected.
              Final terms will be provided upon application approval.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
